import i18n from 'i18next'
import Backend from 'i18next-http-backend'
import LanguageDetector from 'i18next-browser-languagedetector'
import { initReactI18next } from 'react-i18next'
import { runtimeConfig } from '@app/config/runtime'
import { sharedAssets } from '@shared/assets'

const canonicalDocumentLanguages = new Set([
  'de',
  'en',
  'es',
  'fr',
  'ja',
  'ko',
  'ru',
  'vi',
  'zh',
])

export function resolveDocumentLanguage(language: string | undefined) {
  const fallbackLanguage = runtimeConfig.i18n.fallbackLanguage || 'en'
  const candidateLanguage = language || runtimeConfig.i18n.defaultLanguage || fallbackLanguage

  if (candidateLanguage.toLowerCase().split('-')[0] === 'es') {
    return 'es'
  }

  if (candidateLanguage === 'cn' || candidateLanguage === 'zh-CN' || candidateLanguage.startsWith('zh-')) {
    return 'zh'
  }

  if (candidateLanguage === 'vi-VN') {
    return 'vi'
  }

  if (candidateLanguage === 'en-US') {
    return 'en'
  }

  const baseLanguage = candidateLanguage.includes('-')
    ? candidateLanguage.split('-')[0]
    : candidateLanguage

  if (canonicalDocumentLanguages.has(baseLanguage)) {
    return baseLanguage
  }

  return fallbackLanguage.includes('-') ? fallbackLanguage.split('-')[0] : fallbackLanguage
}

export function syncDocumentLanguage(language: string | undefined) {
  if (typeof document === 'undefined') {
    return
  }

  document.documentElement.lang = resolveDocumentLanguage(language)
}

i18n.on('languageChanged', (language) => {
  syncDocumentLanguage(language)
})

void i18n
  .use(Backend)
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    backend: {
      loadPath: sharedAssets.locale('{{lng}}', '{{ns}}'),
    },
    defaultNS: 'translation',
    detection: {
      caches: ['localStorage'],
      order: ['querystring', 'localStorage', 'navigator', 'htmlTag'],
      convertDetectedLanguage: (language) => (
        language.toLowerCase().split('-')[0] === 'es' ? 'es' : language
      ),
    },
    fallbackLng: runtimeConfig.i18n.fallbackLanguage,
    interpolation: {
      escapeValue: false,
    },
    keySeparator: false,
    load: 'currentOnly',
    ns: runtimeConfig.i18n.namespaces,
    react: {
      useSuspense: false,
    },
    supportedLngs: runtimeConfig.i18n.supportedLanguages,
  })
  .then(() => {
    syncDocumentLanguage(i18n.resolvedLanguage || i18n.language)
  })

syncDocumentLanguage(runtimeConfig.i18n.defaultLanguage)

export default i18n
